import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { formatDateTime, RESOLUTION_LABELS } from "@/lib/aiPlanUtils";

/**
 * v1.3 Wave 2 — a single deterministic recommendation row. Adapts to two
 * recommendation shapes that come out of the backend plan builders:
 *   - sick_day / reschedule_single: has `resolution_type` + `reasoning`
 *     (grounded, plain-English reasoning computed in Python — never an
 *     LLM guess).
 *   - weather_cancellation: a plainer {student_name, start_time} summary
 *     row, with no resolution/reasoning fields.
 */
export const ActionPlanRecommendationItem = ({ recommendation }) => {
  const rec = recommendation || {};
  const hasResolution = Boolean(rec.resolution_type);
  const isUnresolved = rec.resolution_type === "unresolved";
  const Icon = !hasResolution ? Clock : isUnresolved ? AlertTriangle : CheckCircle2;
  const iconClass = !hasResolution
    ? "text-muted-foreground"
    : isUnresolved
    ? "text-[hsl(var(--ai-risk))]"
    : "text-[hsl(var(--ai-executed))]";

  const originalTime = rec.original_start_time || rec.start_time;
  const showsMove = rec.new_start_time && rec.new_start_time !== rec.original_start_time;

  return (
    <div className="mt-2 rounded-lg border bg-background/40 p-2" data-testid="ai-action-plan-recommendation-item">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 shrink-0 ${iconClass}`} />
          {rec.student_name || "Lesson"}
        </p>
        {hasResolution && (
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            {RESOLUTION_LABELS[rec.resolution_type] || rec.resolution_type}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {formatDateTime(originalTime)}
        {showsMove ? ` \u2192 ${formatDateTime(rec.new_start_time)}` : ""}
        {rec.new_instructor_name ? ` with ${rec.new_instructor_name}` : ""}
      </p>
      {rec.reasoning && <p className="mt-1 text-xs leading-5 text-muted-foreground">{rec.reasoning}</p>}
    </div>
  );
};
